import { inject, Injectable } from '@angular/core';
import { TippyService } from '@ngneat/helipopper';
import { MenuItem, TreeNode } from '@openng/optimus-ui/api';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { DatabaseUser } from 'src/modules/database/shared/database-user.entity';
import { ChangeSummary } from 'src/modules/enaio/netgen/ChangeSummary';
import { Holiday } from 'src/modules/enaio/netgen/Holiday';
import { NetworkLinkedDocument } from 'src/modules/enaio/netgen/NetworkLinkedDocument';
import { NetworkLinkedDocumentCategory } from 'src/modules/enaio/netgen/NetworkLinkedDocumentCategory';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { TimedCache } from 'src/modules/sm-base/shared/timed-cache.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Dates } from 'src/modules/utils/shared/dates';
import { Objects } from 'src/modules/utils/shared/objects';
import { Strings } from 'src/modules/utils/shared/strings';
import { Utils } from 'src/modules/utils/shared/utils';
import { EditCalendarEventAppointmentComponent } from '../components/edit-calendar-event-appointment/edit-calendar-event-appointment.component';
import { EditCalendarEventForProjectComponent } from '../components/edit-calendar-event-for-project/edit-calendar-event-for-project.component';
import { EditNetworkLinkedDocumentComponent } from '../components/edit-network-linked-document/edit-network-linked-document.component';
import { EditProjectResponsibilitiesComponent } from '../components/edit-project-responsibilities/edit-project-responsibilities.component';
import { EditProjectComponent } from '../components/edit-project/edit-project.component';
import { ProjectPlanningTools } from '../models/project-planning-tools.model';
import { Calendar } from '../netgen/Calendar';
import { CalendarEvent } from '../netgen/CalendarEvent';
import { CalendarEventKind } from '../netgen/CalendarEventKind';
import { CalendarEventTemplate } from '../netgen/CalendarEventTemplate';
import { CalendarEventType } from '../netgen/CalendarEventType';
import { CalendarOutlineLevel } from '../netgen/CalendarOutlineLevel';
import { CalendarOutlineTemplate } from '../netgen/CalendarOutlineTemplate';
import { CalendarRole } from '../netgen/CalendarRole';
import { CreateProjectDto } from '../netgen/CreateProjectDto';
import { EnaioProject } from '../netgen/EnaioProject';
import { Project } from '../netgen/Project';
import { ProjectCustomFieldDefinition } from '../netgen/ProjectCustomFieldDefinition';
import { ProjectDto } from '../netgen/ProjectDto';
import { ProjectPersonalSummary } from '../netgen/ProjectPersonalSummary';
import { ProjectTemplate } from '../netgen/ProjectTemplate';
import { ProjectType } from '../netgen/ProjectType';
import { Resource } from '../netgen/Resource';
import { ResourceType } from '../netgen/ResourceType';

@Injectable({
  providedIn: 'root'
})
export class ProjectPlanningService {

    app = inject(MainAppService);
    tippyService = inject(TippyService);

    personalSummary: ProjectPersonalSummary;
    
    users: DatabaseUser[];
    roles: CalendarRole[];
    projects: Project[];
    resources: Resource[];
    calendars: Calendar[];
    eventTemplates: CalendarEventTemplate[];
    eventTypes: CalendarEventType[];
    outlineLevels: CalendarOutlineLevel[];
    outlineTemplates: CalendarOutlineTemplate[];
    holidays: Holiday[];
    resourceTypes: ResourceType[];
    projectTypes: ProjectType[];
    projectCustomFieldDefinitions: ProjectCustomFieldDefinition[];
    projectTemplates: ProjectTemplate[];
    networkLinkedDocuments: NetworkLinkedDocument[];
    networkLinkedDocumentCategories: NetworkLinkedDocumentCategory[];
    
    private enaioProjects = new TimedCache<EnaioProject[]>(60_000 * 10, async () => {
        try {
            return await RestEndpoint.main().run("api/projectplanning/enaioProject").list(EnaioProject);  
        }
        catch (e) {
            console.error("Failed to load enaio projects", e);
            return [];
        }
    });
    
    constructor() {
        GuiUtils.checkSingleton(this);
    }

    async trackChanges(summary: ChangeSummary): Promise<void> {
        if (summary.hasAnyType(["DatabaseUser", "DatabaseUserGroup", "Company"])) {
            await this.getUsers(true);
        }
        if (summary.hasType("CalendarRole")) {
            await this.getRoles(true);
        }
        if (summary.hasType("Project")) {
            await this.getProjects(true);
        }
        if (summary.hasType("Resource")) {
            await this.getResources(true);
        }
        if (summary.hasType("CalendarEventTemplate")) {
            await this.getEventTemplates(true);
        }
        if (summary.hasType("CalendarEventType")) {
            await this.getEventTypes(true);
        }
        if (summary.hasType("CalendarOutlineLevel")) {
            await this.getOutlineLevels(true);
        }
        if (summary.hasType("CalendarOutlineTemplate")) {
            await this.getOutlineTemplates(true);
        }
        if (summary.hasType("Holiday")) {
            await this.getHolidays(true);
        }
        if (summary.hasType("ResourceType")) {
            await this.getResourceTypes(true);
        }
        if (summary.hasType("ProjectType")) {
            await this.getProjectTypes(true);
        }
        if (summary.hasType("ProjectCustomFieldDefinition")) {
            await this.getProjectCustomFieldDefinitions(true);
        }
        if (summary.hasType("ProjectTemplate")) {
            await this.getProjectTemplates(true);
        }
        if (summary.hasType("NetworkLinkedDocument")) {
            await this.getNetworkLinkedDocuments(true);
        }
        if (summary.hasType("NetworkLinkedDocumentCategory")) {
            await this.getNetworkLinkedDocumentCategories(true);
        }
    }

    async getPersonalSummary(force = false): Promise<ProjectPersonalSummary> {
        if (this.personalSummary == null || force) {
            this.personalSummary = await RestEndpoint.main().run("api/projectplanning/projectPersonalSummary").get(ProjectPersonalSummary);
        }
        return this.personalSummary;
    }

    getEventType(id: number): CalendarEventType {
        if (this.eventTypes == null) {
            throw new Error(this.app.t("ProjectPlanning.errorEventTypesNotLoaded"));
        }
        return this.eventTypes.find(item => item.id == id);
    }

    getProjectType(id: number): ProjectType {
        if (this.projectTypes == null) {
            throw new Error(this.app.t("ProjectPlanning.errorProjectTypesNotLoaded"));
        }
        return this.projectTypes.find(item => item.id == id);
    }

    getResourceType(id: number): ResourceType {
        if (this.resourceTypes == null) {
            throw new Error(this.app.t("ProjectPlanning.errorResourceTypesNotLoaded"));
        }
        return this.resourceTypes.find(item => item.id == id);
    }

    getCurrentUser(): DatabaseUser {
        return this.users.find(u => Strings.equalsCi(u.userName, this.app.getUserName()));
    }

    async getActiveUsers(force = false): Promise<DatabaseUser[]> {
        return (await this.getUsers(force)).filter(u => u.active);
    }

    async getUsers(force = false): Promise<DatabaseUser[]> {
        return this.reloadData("users", () => RestEndpoint.main().run("api/app/user").list(DatabaseUser), force);
    }

    async getRoles(force = false): Promise<CalendarRole[]> {
        return this.reloadData("roles", () => RestEndpoint.main().run("api/projectplanning/calendarRole").list(CalendarRole), force);
    }

    async getEventTypes(force = false): Promise<CalendarEventType[]> {
        return this.reloadData("eventTypes", () => RestEndpoint.main().run("api/projectplanning/calendarEventType").list(CalendarEventType), force);
    }

    async getEventTemplates(force = false): Promise<CalendarEventTemplate[]> {
        return this.reloadData("eventTemplates", () => RestEndpoint.main().run("api/projectplanning/calendarEventTemplate").list(CalendarEventTemplate), force);
    }

    async getOutlineLevels(force = false): Promise<CalendarOutlineLevel[]> {
        return this.reloadData("outlineLevels", () => RestEndpoint.main().run("api/projectplanning/calendarOutlineLevel").list(CalendarOutlineLevel), force);
    }

    async getOutlineTemplates(force = false): Promise<CalendarOutlineTemplate[]> {
        return this.reloadData("outlineTemplates", () => RestEndpoint.main().run("api/projectplanning/calendarOutlineTemplate").list(CalendarOutlineTemplate), force);
    }

    async getProjectTypes(force = false): Promise<ProjectType[]> {
        return this.reloadData("projectTypes", () => RestEndpoint.main().run("api/projectplanning/projectType").list(ProjectType), force);
    }

    async getProjectCustomFieldDefinitions(force = false): Promise<ProjectCustomFieldDefinition[]> {
        return this.reloadData("projectCustomFieldDefinitions", async () => Arrays.sort(await RestEndpoint.main().run("api/projectplanning/projectCustomFieldDefinition").list(ProjectCustomFieldDefinition)), force);
    }

    async getProjectTemplates(force = false): Promise<ProjectTemplate[]> {
        return this.reloadData("projectTemplates", () => RestEndpoint.main().run("api/projectplanning/projectTemplate").list(ProjectTemplate), force);
    }

    async getProjects(force = false): Promise<Project[]> {
        return this.reloadData("projects", () => RestEndpoint.main().run("api/projectplanning/project").list(Project), force);
    }

    async getResources(force = false): Promise<Resource[]> {
        return this.reloadData("resources", () => RestEndpoint.main().run("api/projectplanning/resource").list(Resource), force);
    }

    async getCalendars(force = false): Promise<Calendar[]> {
        return this.reloadData("calendars", () => RestEndpoint.main().run("api/projectplanning/calendar").list(Calendar), force);
    }

    async getResourceTypes(force = false): Promise<ResourceType[]> {
        return this.reloadData("resourceTypes", () => RestEndpoint.main().run("api/projectplanning/resourceType").list(ResourceType), force);
    }

    async getNetworkLinkedDocuments(force = false): Promise<NetworkLinkedDocument[]> {
        return this.reloadData("networkLinkedDocuments", () => RestEndpoint.main().run("api/app/networkLinkedDocument").list(NetworkLinkedDocument), force);
    }

    async getNetworkLinkedDocumentCategories(force = false): Promise<NetworkLinkedDocumentCategory[]> {
        return this.reloadData("networkLinkedDocumentCategories", () => RestEndpoint.main().run("api/app/networkLinkedDocumentCategory").list(NetworkLinkedDocumentCategory), force);
    }

    async getHolidays(force = false): Promise<Holiday[]> {
        return this.reloadData("holidays", () => RestEndpoint.main().run("api/app/holiday").list(Holiday), force);
    }

    async reloadData<K extends keyof this>(key: K, loader: () => Promise<this[K]>, force = false): Promise<this[K]>
    {
        if (this[key] == null || force)
        {
            this[key] = await loader();
        }
        return this[key];
    }

    async getProjectDtos(events: boolean): Promise<ProjectDto[]> {
        return RestEndpoint.main().query({dto: true, dtoEvents: events}).run("api/projectplanning/project").list(ProjectDto);
    }

    async getProjectDto(projectId: number, getEvents = true, eventIds: number[] = null): Promise<ProjectDto> {
        return RestEndpoint.main().query(Objects.removeNullValues({id: projectId, dto: true, dtoEvents: getEvents, eventIds})).run("api/projectplanning/project").get(ProjectDto);
    }

    async getEnaioProjects(): Promise<EnaioProject[]> {
        return this.enaioProjects.get();
    }
    
    async getEventsForCalendars(calendarIds: number[]): Promise<CalendarEvent[]> {
        return calendarIds.length == 0 ? [] : RestEndpoint.main().query({ calendarIds: Arrays.itemsToString(calendarIds, ", ") }).run("api/projectplanning/calendarEvent").list(CalendarEvent);
    }

    async getEventFull(eventId: number): Promise<CalendarEvent> {
        return RestEndpoint.main().query({ id: eventId }).run("api/projectplanning/calendarEvent").get(CalendarEvent);
    }

    private async getEventForAction(eventId: number, projectChanged: () => any): Promise<CalendarEvent | null> {
        let evt = await this.getEventFull(eventId);
        if (evt == null) {
            await this.app.dialog.info(this.app.t("ProjectPlanning.globalSearchEventNotFound", { eventId }), this.app.t("general.warning"));
            await projectChanged();
            return null;
        }
        return evt;
    }

    formatStructurePath(path: string): string[] {
        return Strings.split(path, "|").map(id => Utils.toNumber(id, false, true, 0)).map(id => this.outlineLevels.find(l => l.id == id)?.title ?? "???");
    }

    formatStructurePathString(path: string, sep = " -> "): string {
        return Arrays.itemsToString(this.formatStructurePath(path), sep);
    }

    isProjectNumberKnown(projectNumber: string): boolean {
        let result = this.enaioProjects.data?.some(p => p.projectNumber == projectNumber);
        void this.getEnaioProjects();
        return result;
    }

    async finishEvent(evt: CalendarEvent, user: DatabaseUser): Promise<boolean> {
        if (evt.subTasks?.some(st => !st.finished)) {
            if (!await this.app.dialog.yesNo(this.app.t("ProjectPlanning.finishTaskWithOpenSubTasksConfirmation"), this.app.t("ProjectPlanning.information"))) {
                return false;
            }
        }
        if (evt.doneTime == 0 && evt.needsDoneTimeBeforeFinish(user)) {
            let doneTime = await this.app.dialog.input<number>(new FrontendField("", this.app.t("entity.CalendarEvent.fields.doneTime"), FrontendFieldType.decimal, {
                mandatory: true,
                spinnerStep: 0.25,
                allowOnlySpinnerStepValues: true            
            }), this.app.t("ProjectPlanning.doneTimeRequiredBeforeFinish"));
            if (!(doneTime > 0)) {
                return false;
            }
            evt.doneTime = doneTime;
        }

        evt.finished = true;
        return true;
    }

    reopenEvent(evt: CalendarEvent): boolean {
        evt.finished = false;
        return true;
    }

    finishProject(evt: Project, user: DatabaseUser): boolean {
        evt.finished = true;
        return true;
    }

    reopenProject(p: Project): boolean {
        p.finished = false;
        return true;
    }

    getUserShortName(id: number): string {
        return id == 0 || id == null ? "" : this.users.find(u => u.id == id)?.getAttributeValue("shortName") ?? "<???>";
    }

    getUserShortNameByUserName(userName: string, returnUserNameIfNoShortName = false): string {
        if (Utils.isNoe(userName)) {
            return "";
        }
        let user = this.users.find(u => Strings.equalsCi(u.userName, userName));
        let sn = user?.getAttributeValue("shortName");
        return !Utils.isNoe(sn) ? sn : returnUserNameIfNoShortName ? userName : "<???>";
    }

    getEventContextMenuItems(evt: CalendarEvent, user: DatabaseUser, projectChanged: () => any, view: ComponentView): MenuItem[] {
        let project = this.projects?.find(p => p.id == evt.projectId);
        let result = Arrays.withoutNull([
            {
                label: this.app.t("ProjectPlanning.edit"),
                icon: "fas fa-pen",
                command: () => this.editEvent(evt, projectChanged)
            },
            evt.isCopyAllowed(user) ? {
                label: this.app.t("ProjectPlanning.copy"),
                icon: "fas fa-copy",
                command: () => this.copyEvent(evt, projectChanged)  
            } : null,
            evt.isFinishAllowed(user) ? {
                label: this.app.t("ProjectPlanning.finish"),
                icon: "fas fa-check",
                command: () => this.finishEventFromMenu(evt, user, projectChanged, view)  
            } : null,
            evt.isReopenAllowed(user) ? {
                label: this.app.t("ProjectPlanning.reopen"),
                icon: "fas fa-check",
                command: () => this.reopenEventFromMenu(evt, user, projectChanged, view)  
            } : null,
            evt.isDeleteAllowed(user) ? {
                label: this.app.t("general.delete"),
                icon: "fas fa-trash",
                command: () => this.deleteEvent(evt, projectChanged, view)  
            } : null,
            project != null ? {
                label: this.app.t("ProjectPlanning.assignResponsibilities"),
                icon: "fas fa-users",
                command: () => this.editProjectRoles(project, projectChanged)
            } : null            
        ]);
        return result;
    }

    getProjectContextMenuItems(project: Project, user: DatabaseUser, projectsChanged: () => any, projectChanged: () => any, view: ComponentView): MenuItem[] {
        let result = Arrays.withoutNull([
            {
                label: this.app.t("ProjectPlanning.edit"),
                icon: "fas fa-pen",
                command: () => this.editProject(project, projectsChanged, projectChanged)
            },
            {
                label: this.app.t("ProjectPlanning.assignResponsibilities"),
                icon: "fas fa-users",
                command: () => this.editProjectRoles(project, projectChanged)  
            },
            {
                label: this.app.t("ProjectPlanning.editNote"),
                icon: "fas fa-pen",
                command: () => this.editProjectNote(project, projectChanged, view)  
            }
        ]);
        return result;
    }

    async editProject(project: Project, projectsChanged: () => any, projectChanged: () => any): Promise<void> {
        let result = await this.app.dialog.component(EditProjectComponent, this.app.t("ProjectPlanning.editProject"), { width: "95%" }, {
            id: project.id
        });  
        await this.getProjects(true);
        if (result.buttonClicked == "delete") {
            projectsChanged(); 
        }
        else {
            projectChanged(); 
        }
    }

    async editProjectNote(project: Project, projectChanged: () => any, view: ComponentView): Promise<void> {
        let form = this.app.tForm("entity.Project.fields", new FrontendForm([new FrontendField("description", "", FrontendFieldType.textArea, {
            value: project.description,
            fixedHeight: "75vh"
        })]));
        if (await this.app.dialog.form(form, this.app.t("ProjectPlanning.editProjectNote"), null, true, true) == "ok") {
            project.description = form.getValueString("description");
            await this.app.saveDataHandler(async () => RestEndpoint.main().body(Objects.fromPlain(CreateProjectDto, {
                project,
                propertiesToUpdate: ["description"]
            })).put().run(ProjectPlanningTools.definitionProject.apiEndpoint).getText(), view);
        }
        
        await this.getProjects(true);
        projectChanged(); 
    }

    async editProjectRoles(project: Project, projectChanged: () => any): Promise<void> {
        await this.app.dialog.component(EditProjectResponsibilitiesComponent, this.app.t("ProjectPlanning.assignResponsibilities"), { width: "95%" }, {
            id: project.id
        });  
        await this.getProjects(true);
        projectChanged();
    }
    
    async editEvent(evt: CalendarEvent, projectChanged: () => any): Promise<void> {
        evt = await this.getEventForAction(evt.id, projectChanged);
        if (evt == null) {
            return;
        }
        await this.app.dialog.component<any>(evt.kind == CalendarEventKind.appointment ? EditCalendarEventAppointmentComponent : EditCalendarEventForProjectComponent, evt.kind == CalendarEventKind.appointment ? this.app.t("ProjectPlanning.editAppointment") : this.app.t("ProjectPlanning.editTask"), null, {
            id: evt.id,
            item: evt
        });
        await projectChanged();   
    }

    async copyEvent(evt: CalendarEvent, projectChanged: () => any): Promise<void> {
        evt = await this.getEventForAction(evt.id, projectChanged);
        if (evt == null) {
            return;
        }
        evt.prepareForCopy();
        await this.app.dialog.component<any>(evt.kind == CalendarEventKind.appointment ? EditCalendarEventAppointmentComponent : EditCalendarEventForProjectComponent, evt.kind == CalendarEventKind.appointment ? this.app.t("ProjectPlanning.copyAppointment") : this.app.t("ProjectPlanning.copyTask"), null, {
            id: 0,
            item: evt,
            isCopy: true
        });
        await projectChanged();   
    }

    async finishEventFromMenu(evt: CalendarEvent, user: DatabaseUser, projectChanged: () => any, view: ComponentView): Promise<void> {
        evt = await this.getEventForAction(evt.id, projectChanged);
        if (evt == null) {
            return;
        }
        if (await this.finishEvent(evt, user)) {
            await this.app.saveDataHandler(async () => RestEndpoint.main().upsert(evt).run(ProjectPlanningTools.definitionCalendarEvent.apiEndpoint).getText(), view, true, false);
            await projectChanged();   
        }    
    }

    async reopenEventFromMenu(evt: CalendarEvent, user: DatabaseUser, projectChanged: () => any, view: ComponentView): Promise<void> {
        evt = await this.getEventForAction(evt.id, projectChanged);
        if (evt == null) {
            return;
        }
        if (this.reopenEvent(evt)) {
            await this.app.saveDataHandler(async () => RestEndpoint.main().upsert(evt).run(ProjectPlanningTools.definitionCalendarEvent.apiEndpoint).getText(), view, true, false);
            await projectChanged();   
        }    
    }

    async deleteEvent(evt: CalendarEvent, projectChanged: () => any, view: ComponentView): Promise<void> {
        if (!await this.app.dialog.yesNo(this.app.t("ProjectPlanning.deleteEventConfirmation"), this.app.t("ProjectPlanning.deleteEventTitle"))) {
            return;
        }
        await ProjectPlanningTools.definitionCalendarEvent.deleteDataHandler(evt.id, this.app, view);
        await projectChanged();   
    }

    getNetworkLinkedDocumentContextMenuItems(doc: NetworkLinkedDocument, user: DatabaseUser, changed: () => any, view: ComponentView): MenuItem[] {
        let result = Arrays.withoutNull([
            {
                label: this.app.t("ProjectPlanning.edit"),
                icon: "fas fa-pen",
                command: () => this.editNetworkLinkedDocument(doc, changed)
            },
            {
                label: this.app.t("general.delete"),
                icon: "fas fa-trash",
                command: () => this.deleteNetworkLinkedDocument(doc, changed, view)  
            }
        ]);
        return result;
    }

    async editNetworkLinkedDocument(doc: NetworkLinkedDocument, onChanged: () => any): Promise<void> {
        await this.app.dialog.component(EditNetworkLinkedDocumentComponent, this.app.t("ProjectPlanning.editDocument"), null, {
            id: doc.id
        });  
       await onChanged();
    }

    async deleteNetworkLinkedDocument(doc: NetworkLinkedDocument, onChanged: () => any, view: ComponentView): Promise<void> {
        if (!await this.app.dialog.yesNo(this.app.t("entity.NetworkLinkedDocument.other.deleteConfirmation"), this.app.t("ProjectPlanning.deleteDocumentTitle"), this.app.t("ProjectPlanning.documentRemoved"))) {
            return;
        }
        await ProjectPlanningTools.definitionNetworkLinkedDocument.deleteDataHandler(doc.id, this.app, view);
        await onChanged();  
    }

    async getGuiTree(sel: TreeNode[]): Promise<TreeNode[]> {
        let resourceTypes = await this.getResourceTypes();
        let thisUser = this.getCurrentUser();
        let useCompanies = this.app.mainConfig.useAdminCompanies;

        let result = [
            ...GuiUtils.generateTree2([thisUser], true, false, (u, g, m) => [
                g("21" + u.id, () => m(u.getFullName(), u))
            ]),
            ...GuiUtils.generateTree2((await this.getActiveUsers()).filter(u => u.id != thisUser.id), true, false, (u, g, m) => {
                let companyKey = u.company?.id ?? "none";
                let group = Arrays.getSafe(u.groups, 0);
                let useUserGroupLevel = u.company?.useUserGroupLevel ?? true;
                return [
                    g("01", () => m(this.app.t("ProjectPlanning.employees"), null, { expanded: true })),
                    useCompanies ? g("10" + companyKey, () => m(u.company?.name ?? this.app.t("ProjectPlanning.noCompany"), u.company, { expanded: true, sortText: u.company?.getSortName() ?? "" })) : null,
                    useUserGroupLevel ? g("11" + (useCompanies ? companyKey + "_" : "") + (group?.id ?? "none"), () => m(group?.name ?? this.app.t("ProjectPlanning.noUserGroup"), group, { sortText: group?.getSortName() ?? "" })) : null,
                    g("21" + u.id, () => m(u.getFullName(), u))
                ];
            }),
            ...GuiUtils.generateTree2(await this.getRoles(), true, false, (r, g, m) => [
                g("02", () => m(this.app.t("entity.CalendarEvent.fields.roles"), null)),
                g("22" + r.id, () => m(r.title, r))
            ]),
            ...GuiUtils.generateTree2(await this.getResources(), true, false, (r, g, m) => [
                g("03", () => m(this.app.t("entity.CalendarEvent.fields.resources"), null)),
                g("13" + resourceTypes.find(rt => rt.id == r.typeId)?.id, () => m(resourceTypes.find(rt => rt.id == r.typeId)?.title, resourceTypes.find(rt => rt.id == r.typeId))),
                g("23" + r.id, () => m(r.title, r))
            ])
        ];
        GuiUtils.updateTreeSelectedColors(result, sel, null);
        return result;
    }

    attachTreeRightClickHandler(nodes: TreeNode[], el: HTMLElement, onRightClick: (node: TreeNode, event: MouseEvent) => void): void {
        if (!el) {
            return;
        }

        for (let node of GuiUtils.flattenTree(nodes)) {

            const li = el.querySelector(`li.p-tree-node[data-id="${node.key}"]`) as HTMLElement;

            if (!li) {
                continue;
            }

            // Doppeltes Registrieren vermeiden
            if ((li as any)._hasContextHandler) {
                continue;
            }

            li.addEventListener('contextmenu', (evt: MouseEvent) => {
                evt.preventDefault();
                evt.stopPropagation();

                onRightClick(node, evt);
            });

            (li as any)._hasContextHandler = true;
        }
    }

    getTableColumn(attribName: "userName" | "title" | "subTasks" | "kind" | "start" | "end" | "plannedTime" | "doneTime"): TableColumn {
        switch (attribName) {
            case "userName":
                return new TableColumn("userName", this.app.t("entity.CalendarEvent.fields.editor"), TableCellType.string, 20, { 
                    keepHtml: true,
                    align: "center"
                });
            case "title": 
                return new TableColumn("title", this.app.t("entity.CalendarEvent.fields.title"), TableCellType.string, 50);
            case "subTasks": 
                return new TableColumn("subTasks", this.app.t("ProjectPlanning.tasks"), TableCellType.string, 8, {
                    align: "center"
                });
            case "kind":
                return new TableColumn("kind", this.app.t("entity.CalendarEvent.fields.kind"), TableCellType.string, 3, {
                    align: "center"
                });
            case "start":
                return new TableColumn("start", this.app.t("entity.CalendarEvent.fields.startDate"), TableCellType.dateTime, 1, {
                    align: "center",
                    valueFormatter: e => e.row.raw instanceof CalendarEvent ? e.row.raw.formatDate(e.value as Date) : Dates.formatDefaultDate(e.value as Date)
                });
            case "end":
                return new TableColumn("end", this.app.t("entity.CalendarEvent.fields.endDate"), TableCellType.dateTime, 1, {
                    align: "center",
                    valueFormatter: e => e.row.raw instanceof CalendarEvent ? e.row.raw.formatDate(e.value as Date) : Dates.formatDefaultDate(e.value as Date)
                });
            case "plannedTime": 
                return ProjectPlanningTools.userCanAccessPlanTime(this.getCurrentUser()) ? new TableColumn("plannedTime", this.app.t("entity.CalendarEvent.fields.plannedTime"), TableCellType.decimal, 5, {
                    numDecimalDigits: 2,
                    expandDecimalDigits: true,
                    align: "center"
                }) : null;
            case "doneTime":
                return new TableColumn("doneTime", this.app.t("entity.CalendarEvent.fields.doneTime"), TableCellType.decimal, 5, {
                    numDecimalDigits: 2,
                    expandDecimalDigits: true,
                    align: "center",
                    editable: true,
                    editableCallback: e => (e.row.raw instanceof CalendarEvent) && e.row.raw.isSetDoneTimeAllowed(this.getCurrentUser()),
                    valueSetter: e => {
                        let value = Utils.roundToDecimal(e.value as number, 0.25);
                        if (value < 0) {
                            return false;
                        }
                        (e.row.raw as CalendarEvent).doneTime = value;
                        e.row.values.doneTime = value; 
                        return true;                   
                    }
                });
            default:
                throw new Error(this.app.t("ProjectPlanning.errorInvalidAttribName", { attribName }));
        }
    }

    getAllEditorsText(events: CalendarEvent[], boldRoles = false, includeRoles = true): string {
        let userStrings: string[] = [];
        let roleStrings: string[] = [];

        for (let evt of events) {
            if (evt.kind == CalendarEventKind.appointment) {
                roleStrings = [...roleStrings, ...evt.instances.map(i => this.getUserShortName(this.users.find(u => u.calendarId == i.calendarId)?.id)).filter(s => s != "")];
            }
            else {
                let rolesIds: number[] = [];
                let userIds: number[] = [];
                
                for (let r of evt.roles ?? []) {
                    if (r.responsibleId == null) {
                        rolesIds.push(r.roleId);
                    }
                    else {
                        userIds.push(r.responsibleId);
                    }
                }
                if (includeRoles) {
                    roleStrings = [...roleStrings, ...rolesIds.map(id => this.roles.find(r => r.id == id)?.title ?? "???")]; 
                }
                userStrings = [...userStrings, ...userIds.map(id => this.getUserShortName(id))];
            }
        }
        return Arrays.itemsToString(Arrays.sort(Arrays.getUnique(roleStrings)), ", ", r => boldRoles ? "<b>" + r + "</b>" : r) + (roleStrings.length > 0 && userStrings.length > 0 ? ", " : "") + 
            Arrays.itemsToString(Arrays.sort(Arrays.getUnique(userStrings)), ", ");
    }


}
